import {Component, HostBinding, OnInit, OnDestroy} from '@angular/core';
import {AppConfigService, IAppConfig} from '../../services/app-config.service';
import {UrlConstants} from '../../../shared/utils/url-constants';
import {AuthService} from '../../services/auth.service';
import {PageDataService} from '../../services/page-data.service';
import {TranslateService} from '@ngx-translate/core';
import {Observable, Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {NavigationEnd, Router } from '@angular/router';


class SidenavLink {
  link?: string;
  icon?: string;           // For Material icons
  imagePath?: string;      // For custom image icons
  label: Observable<string>;
  subLinks?: SidenavLink[];
  isImage: boolean;   
  expanded?: boolean;
}

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss']
})
export class SidenavComponent implements OnInit, OnDestroy {
  @HostBinding('class.sidenav-open') get isOpen() {
    let isOpen: boolean;
    this.pageDataService.sidenavOpen$.subscribe(open => isOpen = open).unsubscribe();
    return isOpen;
  }

  @HostBinding('class.sidenav-collapsed') get isCollapsed() {
    let isCollapsed: boolean;
    this.pageDataService.isCollapsed$.subscribe(collapsed => isCollapsed = collapsed).unsubscribe();
    return isCollapsed;
  }

  @HostBinding('class.is-handset') isHandset = false;

  UrlConstants = UrlConstants;
  settings: IAppConfig = AppConfigService.settings;
  contentHeight = window.innerHeight;
  sidenavLoading = true;
  isDashboard: boolean = false; 

  sidenavLinks: SidenavLink[] = [];
  private previouslyExpandedIndices: number[] = [];
  private isCollapsedSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private isHandsetSubscription?: Subscription;

  constructor(public authService: AuthService,
              public pageDataService: PageDataService,
              private translateService: TranslateService,
              private router: Router) {
  }

  ngOnInit(): void {
    this.updateSidenav();
    
    // Sync expansion panels with active routes
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.sidenavLinks.forEach(link => {
        if (link.subLinks) {
          link.expanded = this.isAnySublinkActive(link);
        }
      });
    });
    
    // Track collapsed state to manage expansion panels
    this.isCollapsedSubscription = this.pageDataService.isCollapsed$.subscribe(collapsed => {
      if (collapsed) {
        // Save currently expanded panels and close them
        this.previouslyExpandedIndices = this.sidenavLinks
          .map((link, index) => (link.subLinks && link.expanded) ? index : -1)
          .filter(index => index !== -1);
        
        this.sidenavLinks.forEach(link => {
          if (link.subLinks) {
            link.expanded = false;
          }
        });
      } else {
        // Restore previously expanded panels
        this.previouslyExpandedIndices.forEach(index => {
          if (this.sidenavLinks[index] && this.sidenavLinks[index].subLinks) {
            this.sidenavLinks[index].expanded = true;
          }
        });
        this.previouslyExpandedIndices = [];
      }
    });

    // Track handset state to manage responsive layouts dynamically
    this.isHandsetSubscription = this.pageDataService.isHandset$.subscribe(isHandset => {
      this.isHandset = isHandset;
    });
  }

  ngOnDestroy(): void {
    this.isCollapsedSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    this.isHandsetSubscription?.unsubscribe();
  }

  updateSidenav() {

    const isProductionRequestUser = this.authService.isProductionRequestUser();
    const isIssuerAdminOrSuperAdmin = this.authService.isIssuerAdminOrSuperAdmin();
    const isSuperAdmin = this.authService.isSuperAdmin();
    const isHelpDesk = this.authService.isHelpDesk();
    const isReadOnly = this.authService.isReadOnly();
    const isEoUser = this.authService.isEoUser();
    const isStandardUser = this.authService.isStandardUser();
    const isAdminOrHelpDesk = this.authService.isAdminOrHelpDesk();
    let subLinks = [];
    this.sidenavLinks = [];

    if (!isProductionRequestUser) {
      this.sidenavLinks.push({
        icon: '',
        imagePath:'../../../../assets/img/homeIcon.png',
        link: UrlConstants.buildUrl(UrlConstants.DASHBOARD),
        label: this.translateService.get('sidebar.home'),
        isImage:true
      });

      this.sidenavLinks.push({
        icon: '',
        imagePath:'../../../../assets/img/trackIcon.png',
        link: UrlConstants.buildUrl(UrlConstants.TRACK_AND_TRACE),
        label: this.translateService.get('sidebar.trackAndTrace'),
        isImage:true
      });
    }
    
    if (AppConfigService.settings?.order?.codeCheckEnabled && !isProductionRequestUser) {
      this.sidenavLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.UID_CHECK),
        icon: 'done_all',
        label: this.translateService.get('sidebar.uidCheck'),
        isImage:true
      });
    }

    if (!isProductionRequestUser) {
      subLinks = [];
      const link: SidenavLink = {
        icon: '',
        imagePath:'../../../../assets/img/entityIcon.png',
        label: this.translateService.get('sidebar.entities.title'),
        isImage: true,
        subLinks
      };
      this.sidenavLinks.push(link);
      
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.ECONOMIC_OPERATORS),
        label: this.translateService.get('sidebar.economicOperators')
      });
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.FACILITIES),
        label: this.translateService.get('sidebar.facilities')
      });
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.MACHINES),
        label: this.translateService.get('sidebar.machines')
      });
      
      link.expanded = this.isAnySublinkActive(link);
    }

    if (!isEoUser && !isProductionRequestUser) {
      subLinks = [];
      const link: SidenavLink = {
        icon: '',
        imagePath:'../../../../assets/img/productionIcon.png',
        label: this.translateService.get('sidebar.products.title'),
        subLinks,
        isImage:true
      };
      this.sidenavLinks.push(link);
      
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.PRODUCTS),
        label: this.translateService.get('sidebar.products.myProducts')
      });
      if (AppConfigService.settings?.productionRequests?.enabled) {
        subLinks.push({
          link: UrlConstants.buildUrl(UrlConstants.PRODUCTION_MY_REQUESTS),
          label: this.translateService.get('sidebar.productionRequests.title')
        });
      }
      
      link.expanded = this.isAnySublinkActive(link);
    }

    if (!isProductionRequestUser) {
      this.sidenavLinks.push({
        icon: 'confirmation_number',
        link: UrlConstants.buildUrl(UrlConstants.STAMPS),
        label: this.translateService.get('sidebar.stamps.title'),
        isImage: false
      });
    }

    if (!isEoUser && !isProductionRequestUser) {
      subLinks = [];
      const link: SidenavLink = {
        icon: '',
        imagePath:'../../../../assets/img/regulationIcon.png',
        label: this.translateService.get('sidebar.regulation.title'),
        subLinks,
        isImage:true
      };
      this.sidenavLinks.push(link);
      
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.DOMAIN),
        label: this.translateService.get('sidebar.products.productDomain')
      });
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.TYPE),
        label: this.translateService.get('sidebar.products.productType')
      });
      
      link.expanded = this.isAnySublinkActive(link);
    }

    if (AppConfigService.settings?.order?.enabled) {
      subLinks = [];
      const link: SidenavLink = {
        icon: '',
        imagePath:'../../../../assets/img/orderIcon.png',
        label: this.translateService.get('sidebar.orders.title'),
        subLinks,
        isImage:true
      };
      this.sidenavLinks.push(link);
      
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.ORDERS, UrlConstants.ORDERS_UNIT),
        label: this.translateService.get('sidebar.orders.unit')
      });
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.ORDERS, UrlConstants.ORDERS_AGGREGATED),
        label: this.translateService.get('sidebar.orders.aggregated')
      });
      
      link.expanded = this.isAnySublinkActive(link);
    }

    if (isAdminOrHelpDesk && AppConfigService.settings?.repository?.enabled) {
      subLinks = [];
      const link: SidenavLink = {
        icon: 'insert_chart_outlined',
        label: this.translateService.get('sidebar.repository'),
        subLinks,
        isImage:false
      };
      this.sidenavLinks.push(link);
      
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.MESSAGES),
        label: this.translateService.get('sidebar.messages')
      });
      
      link.expanded = this.isAnySublinkActive(link);
    }

    if (AppConfigService.settings.exciseWarehouses.enabled && !isProductionRequestUser) {
      subLinks = [];
      const link: SidenavLink = {
        icon: 'store',
        label: this.translateService.get('sidebar.exciseWarehouses.title'),
        subLinks,
        isImage:false
      };
      this.sidenavLinks.push(link);
      
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.EXCISE_WAREHOUSES),
        label: this.translateService.get('sidebar.exciseWarehouses.exciseWarehouses')
      });
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.EXCISE_WAREHOUSES, UrlConstants.EXCISE_WAREHOUSES_DELAYED),
        label: this.translateService.get('sidebar.exciseWarehouses.delayed')
      });
      if (isIssuerAdminOrSuperAdmin) {
        subLinks.push({
          link: UrlConstants.buildUrl(UrlConstants.EXCISE_WAREHOUSES, UrlConstants.EXCISE_WAREHOUSES_VALIDATION),
          label: this.translateService.get('sidebar.exciseWarehouses.validation')
        });
      }
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.EXCISE_WAREHOUSES, UrlConstants.EXCISE_WAREHOUSES_REPORTING),
        label: this.translateService.get('sidebar.exciseWarehouses.report')
      });
      
      link.expanded = this.isAnySublinkActive(link);
    }

    if (!isProductionRequestUser && AppConfigService.settings?.reporting?.enabled) {
      this.sidenavLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.REPORTING),
        icon: 'bar_chart',
        label: this.translateService.get('sidebar.reporting'),
        isImage:false
      });
    }

    if (isIssuerAdminOrSuperAdmin && AppConfigService.settings?.reporting?.enabled) {
      subLinks = [];
      const link: SidenavLink = {
        icon: 'insert_chart_outlined',
        label: this.translateService.get('sidebar.reportingAdvanced.title'),
        subLinks,
        isImage:false
      };
      this.sidenavLinks.push(link);
      
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.ADVANCED_REPORTING, UrlConstants.REGISTRY_REPORT),
        label: this.translateService.get('sidebar.reportingAdvanced.registryReport')
      });
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.ADVANCED_REPORTING, UrlConstants.TAX_REPORT),
        label: this.translateService.get('sidebar.reportingAdvanced.taxReport')
      });
      
      link.expanded = this.isAnySublinkActive(link);
    }

    if (isAdminOrHelpDesk && AppConfigService.settings?.changeLogs?.enabled) {
      this.sidenavLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.CHANGE_LOGS),
        icon: 'beenhere',
        label: this.translateService.get('sidebar.changeLogs'),
        isImage:false
      });
    }

    // NEW: Monitoring menu items
    subLinks = [];
    
    if (isSuperAdmin || isStandardUser) {
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.MONITORING, 'dashboard-reporting'),
        label: this.translateService.get('monitoring.dashboardReporting')
      });
    }

    if (isAdminOrHelpDesk || isStandardUser) {
      subLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.AUDIT_TRAIL),
        label: this.translateService.get('sidebar.auditTrail')
      });
    }

    if (subLinks.length > 0) {
      const monitoringLink: SidenavLink = {
        icon: '',
        imagePath: '../../../../assets/img/monitorIcon.png',
        label: this.translateService.get('sidebar.monitoring'),
        subLinks,
        isImage: true 
      };
      this.sidenavLinks.push(monitoringLink);
      monitoringLink.expanded = this.isAnySublinkActive(monitoringLink);
    }

    if (!isProductionRequestUser && AppConfigService.settings?.users?.enabled) {
      this.sidenavLinks.push({
        link: UrlConstants.buildUrl(UrlConstants.USERS),
        icon: 'target',
        label: this.translateService.get('sidebar.users'),
        isImage:false
      });
    }

    // if (!isProductionRequestUser && AppConfigService.settings?.settings?.enabled) {
    //   subLinks = [];
    //   const settingsLink: SidenavLink = {
    //     icon: 'settings',
    //     label: this.translateService.get('sidebar.settings.title'),
    //     subLinks,
    //     isImage: false
    //   };
    //   this.sidenavLinks.push(settingsLink);

    //   subLinks.push({
    //     link: UrlConstants.buildUrl(UrlConstants.SETTINGS),
    //     label: this.translateService.get('sidebar.settings.userSettings')
    //   });

    //   settingsLink.expanded = this.isAnySublinkActive(settingsLink);
    // }
  }

  toggleSidenav() {
    this.pageDataService.toggleSidenav();
  }

  onExpandPanelClick() {
    if (this.isCollapsed) {
      this.pageDataService.expandSidenav();
    }
  }

  updateHeight() {
    const headerHeight = (document.getElementsByTagName('app-header')?.item(0) as HTMLElement)?.offsetHeight || 0;
    this.contentHeight = window.innerHeight - headerHeight;
  }

  isAnySublinkActive(sidenavLink: SidenavLink): boolean {
    if (!sidenavLink.subLinks) {
      return false;
    }
    const currentUrl = this.router.url.split('?')[0].replace(/\/$/, ""); 
    return sidenavLink.subLinks.some(subLink => {
      if (!subLink.link) return false;
      const link = subLink.link.split('?')[0].replace(/\/$/, "");
      return currentUrl === link || currentUrl.startsWith(link + '/');
    });
  }

  isLinkActive(link: string): boolean {
    if (!link) return false;
    const currentUrl = this.router.url.split('?')[0].replace(/\/$/, "");
    const normalizedLink = link.split('?')[0].replace(/\/$/, "");
    if (normalizedLink === "") {
      return currentUrl === "";
    }
    return currentUrl === normalizedLink || currentUrl.startsWith(normalizedLink + '/');
  }

}
